/**
 * 오디오 파일에서 메타데이터 추출
 * ID3 태그, MP3/WAV 헤더 등에서 아티스트, 제목 정보 읽기
 */

export interface AudioMetadata {
  title?: string;
  artist?: string;
  album?: string;
  duration: number;
  genre?: string;
  year?: number;
}

/**
 * 파일 이름에서 아티스트와 제목 추출
 * 일반적인 패턴: "Artist - Title.mp3" 또는 "Artist_Title.mp3"
 */
function parseFileName(fileName: string): {
  artist?: string;
  title?: string;
} {
  // 확장자 제거
  const nameWithoutExt = fileName.replace(/\.(mp3|wav|m4a|flac|ogg)$/i, "");

  // 패턴 1: "Artist - Title"
  if (nameWithoutExt.includes(" - ")) {
    const [artist, title] = nameWithoutExt.split(" - ");
    return { artist: artist.trim(), title: title.trim() };
  }

  // 패턴 2: "Artist_Title"
  if (nameWithoutExt.includes("_")) {
    const [artist, title] = nameWithoutExt.split("_");
    return { artist: artist.trim(), title: title.trim() };
  }

  // 패턴 3: 숫자로 시작하면 제목만 (예: "01 Title.mp3")
  if (/^\d+\s*[-.]?\s*/.test(nameWithoutExt)) {
    const title = nameWithoutExt.replace(/^\d+\s*[-.]?\s*/, "");
    return { title: title.trim() };
  }

  // 기본: 전체를 제목으로
  return { title: nameWithoutExt.trim() };
}

/**
 * Web Audio API로 duration 정확히 측정
 */
async function getDuration(audioFile: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const audio = new Audio();
    const url = URL.createObjectURL(audioFile);

    audio.addEventListener("loadedmetadata", () => {
      URL.revokeObjectURL(url);
      resolve(audio.duration);
    });

    audio.addEventListener("error", () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load audio"));
    });

    audio.src = url;
  });
}

/**
 * 오디오 파일에서 메타데이터 추출 (클라이언트 사이드)
 */
export async function extractMetadata(
  audioFile: File
): Promise<AudioMetadata> {
  try {
    // 1. Duration 측정
    let duration = 0;
    try {
      duration = await getDuration(audioFile);
    } catch (error) {
      console.warn("⚠️ Duration 측정 실패, 파일 크기로 추정");
      duration = Math.floor((audioFile.size / 1024 / 1024) * 60);
    }

    // 2. 파일 이름에서 정보 추출
    const { artist, title } = parseFileName(audioFile.name);

    return {
      title,
      artist,
      duration,
    };
  } catch (error) {
    console.error("❌ 메타데이터 추출 오류:", error);
    return {
      duration: Math.floor((audioFile.size / 1024 / 1024) * 60),
    };
  }
}

